// Chart — minimal SVG renderer for the Funnel query catalog.
//
// Three layouts: bar (vertical), line (with axis), donut. All driven by the
// same data shape: columns + rows. The chart picks the right x/y from the
// CatalogEntry's ChartSpec; no big chart library — keeps the bundle small
// and the SVG clean for the PNG export path.
//
// Brand palette only: teal, mint, coral, amber. Donut iterates through the
// palette. Tooltips on hover via simple title elements.

import { forwardRef, useMemo } from "react";

import type { ChartSpec, QueryRunResult } from "../lib/query-catalog";

interface ChartProps {
  spec: ChartSpec;
  result: QueryRunResult;
}

const W = 760;
const H = 320;
const PAD = { top: 18, right: 24, bottom: 56, left: 64 };

const PALETTE = [
  "#7a3fb0",
  "#6fefd0",
  "#e85a9f",
  "#f6b13c",
  "#cfe0e6",
  "#46c8ff",
  "#9a8cff",
  "#d96aa6",
];

export const Chart = forwardRef<SVGSVGElement, ChartProps>(function Chart({ spec, result }, ref) {
  if (result.rows.length === 0) {
    return (
      <div
        style={{
          padding: "60px 20px",
          textAlign: "center",
          color: "var(--ink-faint)",
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          letterSpacing: "0.16em",
        }}
      >
        QUERY RETURNED NO ROWS
      </div>
    );
  }

  if (spec.type === "donut") {
    return <DonutChart spec={spec} result={result} ref={ref} />;
  }
  if (spec.type === "line") {
    return <LineChart spec={spec} result={result} ref={ref} />;
  }
  return <BarChart spec={spec} result={result} ref={ref} />;
});

// ============================================================================
// Bar
// ============================================================================

const BarChart = forwardRef<SVGSVGElement, ChartProps>(function BarChart({ spec, result }, ref) {
  const series = useSeries(spec, result);
  const max = Math.max(1, ...series.map((s) => s.y));
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const bandW = innerW / series.length;
  const barW = Math.max(8, bandW * 0.62);

  const ticks = niceTicks(max, 5);

  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      style={{
        background: "var(--bg-0)",
        borderRadius: 11,
        fontFamily: "var(--font-display)",
      }}
    >
      {/* y-axis ticks + gridlines */}
      {ticks.map((t) => {
        const y = PAD.top + innerH - (t / max) * innerH;
        return (
          <g key={t}>
            <line
              x1={PAD.left}
              y1={y}
              x2={W - PAD.right}
              y2={y}
              stroke="rgba(150, 172, 190, 0.08)"
            />
            <text
              x={PAD.left - 8}
              y={y + 4}
              textAnchor="end"
              fontSize={10}
              fill="var(--ink-faint)"
              fontFamily="var(--font-mono)"
            >
              {fmtCompact(t)}
            </text>
          </g>
        );
      })}
      {/* axis baseline */}
      <line
        x1={PAD.left}
        y1={PAD.top + innerH}
        x2={W - PAD.right}
        y2={PAD.top + innerH}
        stroke="rgba(150, 172, 190, 0.2)"
      />
      {/* bars */}
      {series.map((s, i) => {
        const bh = Math.max(2, (s.y / max) * innerH);
        const x = PAD.left + bandW * i + (bandW - barW) / 2;
        const y = PAD.top + innerH - bh;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={bh} rx={3} fill="var(--teal)" opacity={0.92}>
              <title>
                {s.x}: {fmtFull(s.y)}
                {spec.y_suffix ?? ""}
              </title>
            </rect>
            <text
              x={x + barW / 2}
              y={PAD.top + innerH + 16}
              textAnchor="middle"
              fontSize={10}
              fill="var(--ink-dim)"
              fontFamily="var(--font-mono)"
            >
              {truncate(String(s.x), 14)}
            </text>
          </g>
        );
      })}
      {/* labels */}
      {spec.y_label && (
        <text
          transform={`translate(16, ${PAD.top + innerH / 2}) rotate(-90)`}
          textAnchor="middle"
          fontSize={10}
          fill="var(--ink-faint)"
          fontFamily="var(--font-mono)"
          letterSpacing="0.14em"
        >
          {spec.y_label.toUpperCase()}
        </text>
      )}
      {spec.x_label && (
        <text
          x={PAD.left + innerW / 2}
          y={H - 8}
          textAnchor="middle"
          fontSize={10}
          fill="var(--ink-faint)"
          fontFamily="var(--font-mono)"
          letterSpacing="0.14em"
        >
          {spec.x_label.toUpperCase()}
        </text>
      )}
    </svg>
  );
});

// ============================================================================
// Line
// ============================================================================

const LineChart = forwardRef<SVGSVGElement, ChartProps>(function LineChart({ spec, result }, ref) {
  const series = useSeries(spec, result);
  const max = Math.max(1, ...series.map((s) => s.y));
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const stepX = series.length > 1 ? innerW / (series.length - 1) : innerW;

  const ticks = niceTicks(max, 5);

  const points = series.map((s, i) => ({
    x: PAD.left + stepX * i,
    y: PAD.top + innerH - (s.y / max) * innerH,
    label: s.x,
    value: s.y,
  }));

  const pathD = points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(" ");
  const areaD = `${pathD} L ${points[points.length - 1]?.x} ${PAD.top + innerH} L ${points[0]?.x} ${PAD.top + innerH} Z`;

  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      style={{ background: "var(--bg-0)", borderRadius: 11, fontFamily: "var(--font-display)" }}
    >
      {ticks.map((t) => {
        const y = PAD.top + innerH - (t / max) * innerH;
        return (
          <g key={t}>
            <line
              x1={PAD.left}
              y1={y}
              x2={W - PAD.right}
              y2={y}
              stroke="rgba(150, 172, 190, 0.08)"
            />
            <text
              x={PAD.left - 8}
              y={y + 4}
              textAnchor="end"
              fontSize={10}
              fill="var(--ink-faint)"
              fontFamily="var(--font-mono)"
            >
              {fmtCompact(t)}
            </text>
          </g>
        );
      })}
      <path d={areaD} fill="var(--teal)" opacity={0.12} />
      <path d={pathD} stroke="var(--teal)" strokeWidth={2.4} fill="none" />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={3.5} fill="var(--teal)">
            <title>
              {p.label}: {fmtFull(p.value)}
              {spec.y_suffix ?? ""}
            </title>
          </circle>
          <text
            x={p.x}
            y={PAD.top + innerH + 16}
            textAnchor="middle"
            fontSize={10}
            fill="var(--ink-dim)"
            fontFamily="var(--font-mono)"
          >
            {truncate(String(p.label), 10)}
          </text>
        </g>
      ))}
      {spec.y_label && (
        <text
          transform={`translate(16, ${PAD.top + innerH / 2}) rotate(-90)`}
          textAnchor="middle"
          fontSize={10}
          fill="var(--ink-faint)"
          fontFamily="var(--font-mono)"
          letterSpacing="0.14em"
        >
          {spec.y_label.toUpperCase()}
        </text>
      )}
    </svg>
  );
});

// ============================================================================
// Donut
// ============================================================================

const DonutChart = forwardRef<SVGSVGElement, ChartProps>(function DonutChart({ spec, result }, ref) {
  const series = useSeries(spec, result);
  const total = series.reduce((acc, s) => acc + s.y, 0);
  const cx = 180;
  const cy = H / 2;
  const r = 110;
  const rIn = 64;

  let angle = -Math.PI / 2;
  const arcs = series.map((s, i) => {
    const slice = (s.y / total) * Math.PI * 2;
    const a0 = angle;
    const a1 = angle + slice;
    angle = a1;
    const x0 = cx + Math.cos(a0) * r;
    const y0 = cy + Math.sin(a0) * r;
    const x1 = cx + Math.cos(a1) * r;
    const y1 = cy + Math.sin(a1) * r;
    const xi1 = cx + Math.cos(a1) * rIn;
    const yi1 = cy + Math.sin(a1) * rIn;
    const xi0 = cx + Math.cos(a0) * rIn;
    const yi0 = cy + Math.sin(a0) * rIn;
    const large = slice > Math.PI ? 1 : 0;
    const d = [
      `M ${x0} ${y0}`,
      `A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`,
      `L ${xi1} ${yi1}`,
      `A ${rIn} ${rIn} 0 ${large} 0 ${xi0} ${yi0}`,
      "Z",
    ].join(" ");
    return { d, color: PALETTE[i % PALETTE.length]!, label: s.x, value: s.y };
  });

  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      style={{ background: "var(--bg-0)", borderRadius: 11, fontFamily: "var(--font-display)" }}
    >
      {arcs.map((a, i) => (
        <path key={i} d={a.d} fill={a.color} opacity={0.88}>
          <title>
            {a.label}: {fmtFull(a.value)} ({((a.value / total) * 100).toFixed(1)}%)
          </title>
        </path>
      ))}
      <text
        x={cx}
        y={cy - 6}
        textAnchor="middle"
        fontSize={11}
        fontFamily="var(--font-mono)"
        letterSpacing="0.14em"
        fill="var(--ink-faint)"
      >
        TOTAL
      </text>
      <text
        x={cx}
        y={cy + 18}
        textAnchor="middle"
        fontSize={20}
        fontWeight={600}
        fill="var(--ink)"
        fontFamily="var(--font-display)"
      >
        {fmtCompact(total)}
      </text>
      {/* Legend on the right */}
      {arcs.map((a, i) => {
        const ly = 40 + i * 26;
        return (
          <g key={i}>
            <rect x={360} y={ly - 9} width={12} height={12} rx={2} fill={a.color} />
            <text
              x={380}
              y={ly + 1}
              fontSize={12}
              fill="var(--ink)"
              fontFamily="var(--font-display)"
              dominantBaseline="middle"
            >
              {truncate(String(a.label), 28)}
            </text>
            <text
              x={W - 24}
              y={ly + 1}
              textAnchor="end"
              fontSize={12}
              fill="var(--ink-dim)"
              fontFamily="var(--font-mono)"
              dominantBaseline="middle"
            >
              {fmtFull(a.value)} · {((a.value / total) * 100).toFixed(1)}%
            </text>
          </g>
        );
      })}
    </svg>
  );
});

// ============================================================================
// helpers
// ============================================================================

interface Point {
  x: string | number;
  y: number;
}

function useSeries(spec: ChartSpec, result: QueryRunResult): Point[] {
  return useMemo(() => {
    const xName = spec.x_col ?? spec.label_col ?? result.columns[0];
    const yName = spec.y_col ?? spec.value_col ?? result.columns[1];
    if (!xName || !yName) return [];
    const xIdx = result.columns.indexOf(xName);
    const yIdx = result.columns.indexOf(yName);
    if (xIdx < 0 || yIdx < 0) return [];
    return result.rows.map((r) => ({
      x: r[xIdx] as string | number,
      y: Number(r[yIdx] ?? 0),
    }));
  }, [result, spec]);
}

function fmtCompact(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1);
}

function fmtFull(n: number): string {
  if (Number.isInteger(n)) return n.toLocaleString("en-US");
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

function niceTicks(max: number, count: number): number[] {
  const step = niceStep(max / count);
  const ticks: number[] = [];
  for (let v = 0; v <= max + step / 2; v += step) ticks.push(v);
  return ticks;
}

function niceStep(raw: number): number {
  if (raw === 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  let step: number;
  if (norm < 1.5) step = 1;
  else if (norm < 3) step = 2;
  else if (norm < 7) step = 5;
  else step = 10;
  return step * mag;
}
